<template>
  <div class="step-pay-button">
    <form
      class="form"
      @invalid.capture.prevent="handleInvalid"
      @submit.prevent="handleSubmit"
      @change="handleChange"
    >
      <fieldset class="fieldset">
        <legend class="legend">
          Create an account
        </legend>

        <Email
          v-model="form.email"
          :error="formErrors['email']"
          :label="studentPass ? 'Student Email Address' : undefined"
        />

        <Input
          id="password"
          v-model="form.password"
          label="Password"
          error-code="UserPasswordInvalid"
          autocomplete="new-password"
          minlength="6"
          :error="formErrors.password"
          passwordrules="minlength: 6;"
          :type="showPassword ? 'text' : 'password'"
          autocapitalize="off"
          autocorrect="off"
          required
        />

        <p class="form__note form__note--show-password">
          <button
            title="Show password"
            class="show-password button-link"
            aria-controls="password"
            @click.prevent="showPassword = !showPassword"
          >
            <template v-if="showPassword">
              Hide Password
            </template>
            <template v-if="!showPassword">
              Show Password
            </template>
          </button>
        </p>

        <div
          v-if="regions.length > 1"
          class="form__column form__column--region"
        >
          <label
            class="label"
            for="region"
          >Where do you plan to use Metro Bike Share the most?</label>
          <select
            id="region"
            v-model="form.region"
            class="select select--small"
            :class="formErrors.region ? 'input--error' : ''"
            name="region"
            required
          >
            <option :value="null">
              Select
            </option>
            <option
              v-for="region in regions"
              :key="region.id"
              :value="region.id"
            >
              {{ region.title }}
            </option>
          </select>
          <p
            v-if="formErrors.region"
            class="form__error"
          >
            {{ formErrors.region }}
          </p>
        </div>

        <div
          v-for="agreement in Object.keys(form.userAgreement)"
          :key="agreement"
          class="checkbox checkbox--inline"
        >
          <label
            class="checkbox__label"
            :for="`terms-${agreement}`"
          >
            <input
              :id="`terms-${agreement}`"
              v-model="form.userAgreement[agreement]"
              class="checkbox__input"
              type="checkbox"
              :name="`terms-${agreement}`"
              value="1"
            >
            <p v-html="$t('user-agreement')[agreement]" />
          </label>
        </div>

        <p
          v-if="!optOutEnabled"
          class="opt-out-enabler"
        >
          <button
            @click="optOutEnabled = true"
          >
            Update your email or text message preferences
          </button>
        </p>

        <div v-if="optOutEnabled">
          <div class="checkbox">
            <input
              id="email-opt-in"
              v-model="form.optInEmail"
              class="checkbox__input"
              type="checkbox"
              name="email-opt-in"
              value="1"
            >
            <label
              class="checkbox__label"
              for="email-opt-in"
            >Subscribe to the monthly newsletter</label>
          </div>

          <div
            v-if="allowOptInSms"
            class="checkbox"
          >
            <input
              id="phone-opt-in"
              v-model="form.optInSms"
              class="checkbox__input"
              type="checkbox"
              name="phone-opt-in"
              value="1"
            >
            <label
              class="checkbox__label"
              for="phone-opt-in"
            >Receive text message account notifications</label>
          </div>
        </div>
      </fieldset>
      <p
        v-if="!!passType && passType.isAutoRenewing"
        class="note"
      >
        Your pass will renew automatically<span v-if="passType.duration">
          every {{ passType.duration }} days</span>. Usage fees will be charged to your payment method daily. Cancel
        anytime.
      </p>
      <transition name="slide-down">
        <p
          v-if="paymentError"
          class="form__error"
        >
          {{ paymentError }}
        </p>
      </transition>
      <transition name="slide-down">
        <p
          v-if="registrationError"
          class="form__error"
        >
          {{ registrationError }}
        </p>
      </transition>
      <StripePayButton
        :stripe-key="stripeKey"
        :class="!isFilled ? 'disabled' : ''"
        @token="handlePayButtonToken"
      />
      <input
        type="submit"
        class="button button--submit"
        :disabled="!isFilled"
        value="Continue Signup"
      >
      <PreviousStepButton />
    </form>
    <!-- <hr />
    <p class="hide-if-app-pages">
      <a href="/profile/">Have an account? Log in here</a>
    </p>-->
  </div>
</template>
<script>
import Email from '../components/Email.vue';
import Input from '../components/Input.vue';
import StripePayButton from '../components/StripePayButton.vue';
import PreviousStepButton from '../components/PreviousStepButton.vue';
import { getErrorMessage } from '../utils';

export default {
  components: {
    Email,
    Input,
    StripePayButton,
    PreviousStepButton,
  },
  data() {
    const { user } = this.$store.state;
    return {
      showPassword: false,
      allowOptInSms: user.allowOptInSms !== false,
      form: {
        email: user.email,
        password: user.password,
        userAgreement: Object.fromEntries(
          new Map(
            Object.keys(this.$t('user-agreement')).map(agreement => [
              agreement,
              false,
            ]),
          ),
        ),
        optInSms: this.$store.state.user.optInSms,
        optInEmail: this.$store.state.user.optInEmail,
        region: this.$store.state.user.region,
      },
      optOutEnabled:
        !this.$store.state.user.optInEmail || !this.$store.state.user.optInSms,
      paymentErrorMessage: null,
      formErrors: {},
    };
  },
  computed: {
    registration() {
      return this.$store.state.registration;
    },
    order() {
      return this.$store.state.order;
    },
    stripeKey() {
      return this.$store.state.stripeKey;
    },
    passType() {
      return this.$store.getters.passType;
    },
    studentPass() {
      if (this.$store.state.registration.discountEligibilityType === 'student') {
        return true;
      }
      return false;
    },
    regions() {
      return this.$store.state.regions;
    },
    acceptedEula() {
      return Object.values(this.form.userAgreement).every(
        agreement => agreement === true,
      );
    },
    validatingEmail() {
      return this.$store.getters.isValidatingEmail;
    },
    isFilled() {
      return (
        !this.validatingEmail
        && this.form.email !== ''
        && this.form.password !== ''
        && this.acceptedEula
        && (this.regions.length <= 1 || this.form.region !== null)
      );
    },
    paymentError() {
      const { order } = this.$store.state;
      if (order.errorList && order.errorList.includes('PaymentError')) {
        return 'An error occured processing your payment information. Please confirm your information and try again.';
      }
      if (this.paymentErrorMessage) {
        return this.paymentErrorMessage;
      }
      return null;
    },
    registrationError() {
      const { order } = this.$store.state;
      if (order.errorList) {
        return getErrorMessage(order.errorList);
      }
      return null;
    },
  },
  methods: {
    async handlePayButtonToken(response) {
      if (
        response.token
        && response.token.card
        && response.token.card.funding === 'prepaid'
      ) {
        this.paymentErrorMessage = this.$t('error-prepaid-card');
        response.complete('fail');
        return;
      }
      this.paymentErrorMessage = null;

      this.$store.dispatch('updateUser', {
        email: this.form.email,
        password: this.form.password,
        optInEmail: this.form.optInEmail,
        optInSms: this.form.optInSms,
        firstName: response.payerName
          .trim()
          .split(' ')
          .slice(0, -1)
          .join(' '),
        lastName: response.payerName
          .trim()
          .split(' ')
          .slice(-1)
          .join(''),
        addressStreet: response.shippingAddress.addressLine[0],
        addressStreet2: response.shippingAddress.addressLine.slice(1).join(' '),
        addressCity: response.shippingAddress.city,
        addressState: response.shippingAddress.region,
        zipCode: response.shippingAddress.postalCode,
        region: this.form.region,
        country: response.shippingAddress.country,
      });
      if (!this.$store.state.enableSms) {
        this.$store.dispatch('updateUser', {
          phone: response.payerPhone.replace(/\D/g, ''),
        });
      }
      this.$store.dispatch('updateRegistration', {
        paymentMethod: 'credit',
        walletName: response.walletName,
        stripeToken: response.token,
        fullfillCard: response.shippingOption.id === 'fullfill_card',
      });
      await this.$store.dispatch('submitRegistration');
      // TODO: support errors
      if (this.order.complete) {
        response.complete('success');
      } else {
        response.complete('fail');
      }
      this.routeRegistration();
    },
    routeRegistration() {
      if (this.order.errorList) {
        return;
      }
      this.$router.push({ name: 'orderComplete' });
    },
    handleInvalid(e) {
      this.formErrors = {
        ...this.formErrors,
        [e.target.name]: e.target.validationMessage,
      };
    },
    handleChange(e) {
      this.formErrors = {
        ...this.formErrors,
        [e.target.name]: '',
      };
    },
    async handleSubmit() {
      this.$store.dispatch('updateUser', this.form);
      this.$router.push({
        name: this.$store.getters.nextStep(this.$route.name),
      });
    },
  },
};
</script>
